/**
 * ========================
 * LOCATIONS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu for the Locations module (campuses and everything attached to them).
 * Sections:
 *  1. Overview — Dashboard
 *  2. Campuses — Campuses, Service schedule, Venues, Leaders
 *  3. Settings — Social media platforms
 *
 * Visibility is "role OR right": Admin / LocationsAdmin / "Locations Access", or the matching
 * Location.* right (rights can be granted per user without a role). createVisibilityCheck would
 * AND roles with rights, so the checks are written out here.
 */

import { IconBrandInstagram, IconBuildingChurch, IconCalendarWeek, IconDashboard, IconDoor, IconUserStar } from '@tabler/icons-react';
import { DomainMenuProvider, MenuItemVisibility, MenuSection } from '../../layouts/menu/types';

const MODULE_ROLES = ['Admin', 'admin', 'LocationsAdmin', 'Locations Access'];

const roleOrRight =
  (right: string): MenuItemVisibility =>
  (ctx) =>
    MODULE_ROLES.some((r) => ctx.userRoles.includes(r)) || ctx.userPermissions.includes(right);

class LocationsMenuProvider implements DomainMenuProvider {
  domainId = 'locations';

  getMenuConfig() {
    const viewers = roleOrRight('Location.View');

    const sections: MenuSection[] = [
      {
        id: 'locations-overview',
        label: 'Overview',
        visible: viewers,
        items: [{ id: 'locations-dashboard', label: 'Dashboard', path: '/app/locations', icon: <IconDashboard size={16} />, visible: viewers }],
      },
      {
        id: 'locations-campuses-section',
        label: 'Campuses',
        visible: viewers,
        items: [
          { id: 'locations-campuses', label: 'Campuses', path: '/app/locations/campuses', icon: <IconBuildingChurch size={16} />, visible: viewers },
          {
            id: 'locations-service-schedule',
            label: 'Service schedule',
            path: '/app/locations/service-schedule',
            icon: <IconCalendarWeek size={16} />,
            visible: roleOrRight('Location.ServiceTime.View'),
          },
          { id: 'locations-venues', label: 'Venues', path: '/app/locations/venues', icon: <IconDoor size={16} />, visible: roleOrRight('Location.Venue.View') },
          { id: 'locations-leaders', label: 'Leaders', path: '/app/locations/leaders', icon: <IconUserStar size={16} />, visible: roleOrRight('Location.Leader.View') },
        ],
      },
      {
        id: 'locations-settings',
        label: 'Settings',
        visible: roleOrRight('Location.SocialMedia.View'),
        items: [
          {
            id: 'locations-social-media',
            label: 'Social media platforms',
            path: '/app/locations/social-media',
            icon: <IconBrandInstagram size={16} />,
            visible: roleOrRight('Location.SocialMedia.View'),
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '2.0.0',
        lastUpdated: Date.now(),
        description: 'Locations management menu',
      },
    };
  }
}

export const locationsMenuProvider = new LocationsMenuProvider();
